import { deepSignal } from 'deepsignal';
import CustomizeSection from '@components/overlays/shared/customize-section.jsx';
import { buildCustomizedURL } from '@util/common.js';
import ColorPicker from '@components/pickers/color-picker/color.picker.jsx';
import Picker from '@components/input/picker.input.jsx';
import NumberInput from '@components/input/number.input.jsx';
import LinkSetup from '@components/overlays/social/link-setup/link-setup.jsx';
import {
  LeaderboardOverlay,
  LeaderboardOverlayBackgroundColors,
  toLeaderboardBackgroundColor,
} from '@playlive/overlay-core/overlays/leaderboard';
import { CHARITY } from '@util/const.js';
import ScaledOverlayWrapper from '@components/overlays/wrapper/scaled-overlay-wrapper.jsx';
import HorizontalCustomizeWrapper from '@components/overlays/wrapper/horizonal-customize-wrapper.jsx';
import useCommon from '@hooks/useCommon.js';
import useDemoProgressBarData from '@hooks/useDemoProgressBarData.js';
import ColorPickerLabel from '@components/pickers/color-picker/color-picker-label.jsx';

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  leaderboardColor: 'default-red',
  leaderDonors: 5,
  timeType: 'all',
});

export default function LeaderboardOverlayPage() {
  const {
    overlayInfo,
    overlayURL,
    setConfigState,
    tiltifyConfig,
    twitchConfig,
  } = useCommon({
    config,
    forceTiltify: true,
  });

  const { demoDonations } = useDemoProgressBarData({ showDonors: 'yes' });

  const overlayDashboardURL = buildCustomizedURL({
    config: {
      ...config,
      ...tiltifyConfig,
      ...twitchConfig,
      leaderDonors: 100,
    },
    urlBase: 'leaderboard-dashboard',
    addCharityType: true,
  });

  const timeOptions = [
    {
      value: 'all',
      name: 'All',
      description: 'Will show all donations made to your campaign',
    },
    {
      value: 'daily',
      name: 'Daily',
      description: 'Will show any donations made within the last 24 hours',
    },
    {
      value: 'weekly',
      name: 'Weekly',
      description: 'Will show any donations made within the last seven days',
    },
    {
      value: 'monthly',
      name: 'Monthly',
      description: 'Will show any donations made within the last 31 days',
    },
    {
      value: 'yearly',
      name: 'Yearly',
      description: 'Will show any donations made within the last 365 days',
    },
    {
      value: 'ytd',
      name: 'Year to date',
      description: 'Will show any donations made since January 1',
    },
  ];

  const currentTimeType = timeOptions.find(
    (option) => option.value === config.timeType
  );

  const timeTypeDescription = currentTimeType?.description;

  function filterColors(color) {
    return LeaderboardOverlayBackgroundColors.find((c) => c === color);
  }

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col xl:flex-row! overflow-x-hidden">
        <div className="example_overlay select-none overflow-x-hidden w-full">
          <ScaledOverlayWrapper allowScaling={false} defaultScale="scale-100">
            <LeaderboardOverlay
              donors={demoDonations}
              username="Steve"
              backgroundColor={toLeaderboardBackgroundColor(
                config.leaderboardColor
              )}
              maxShowDonors={Number(config.leaderDonors)}
            />
          </ScaledOverlayWrapper>
        </div>

        <HorizontalCustomizeWrapper className="flex flex-col mb-0!">
          <CustomizeSection title="Appearance" className="flex-grow">
            <ColorPickerLabel
              color={config.leaderboardColor}
              labelText="Leaderboard color"
            />
            <ColorPicker
              fieldName="leaderboardColor"
              currentColor={config.leaderboardColor}
              filterFunction={filterColors}
              onChange={setConfigState}
            />
            <NumberInput
              name="leaderDonors"
              value={Number(config.leaderDonors)}
              placeholder="5"
              onChange={setConfigState}
              isCurrency={false}
              maxValue={50}
              minValue={1}
              suffix="donors"
              labelText="Donor Count"
              description="Amount of donors to display"
            />

            <Picker
              items={timeOptions}
              fieldName="timeType"
              onChange={setConfigState}
              currentValue={config.timeType}
              labelText="Timeframe"
              description={timeTypeDescription}
            />
          </CustomizeSection>

          {/* Charity Section Placeholder */}
        </HorizontalCustomizeWrapper>
      </div>

      <div className="w-full xl:mt-4">
        <LinkSetup
          allowTeamCampaignSelection={true}
          constrainWidth={false}
          charityType={CHARITY.TILTIFY}
          obsDimensions={overlayInfo.obsDimensions}
          overlayName={overlayInfo.name}
          overlayURL={overlayURL}
          finalStepSlotPosition="middle"
          finalStepContent={
            <div className="mt-4">
              <a
                className="pt-4 link"
                rel="noopener noreferrer"
                href={overlayDashboardURL}
                target="_blank"
              >
                Click here to visit the exclusion dashboard
              </a>
            </div>
          }
        />
      </div>
    </div>
  );
}
